import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { hasSupabaseConfig } from '../lib/supabaseClient';
import {
  castVoteBallot,
  getOrCreateDeviceToken,
  getStoredDisplayName,
  joinMeetingRoom,
  setStoredDisplayName,
} from '../utils/voteRemote';

function normalizeOptions(options) {
  if (!Array.isArray(options)) return [];
  return options.map((opt) => {
    if (typeof opt === 'string') {
      return { value: opt, label: formatChoiceLabel(opt) };
    }
    const value = opt.id || opt.value || String(opt);
    const label = opt.label || opt.name || formatChoiceLabel(value);
    return { value: String(value), label };
  });
}

function formatChoiceLabel(value) {
  const v = String(value || '');
  if (v === 'aye' || v === 'yes') return 'Aye';
  if (v === 'no') return 'No';
  if (v === 'abstain') return 'Abstain';
  return v;
}

export default function ParticipantRemotePage() {
  const { joinCode: joinCodeParam } = useParams();
  const joinCode = String(joinCodeParam || '').toUpperCase();

  const [displayName, setDisplayName] = useState(() => getStoredDisplayName());
  const [room, setRoom] = useState(null);
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [castResult, setCastResult] = useState(null);
  const [deviceToken] = useState(() => getOrCreateDeviceToken());
  const [writeInMode, setWriteInMode] = useState(false);
  const [writeInName, setWriteInName] = useState('');

  const refresh = useCallback(async () => {
    if (!hasSupabaseConfig()) {
      setError('Voting remote requires Supabase configuration.');
      return;
    }
    try {
      const data = await joinMeetingRoom(joinCode);
      setRoom(data);
      setSession(data?.openSession || null);
      setError(null);
      if (!data?.openSession) {
        setCastResult(null);
      }
    } catch (err) {
      setError(err?.message || 'Could not join room');
      setRoom(null);
      setSession(null);
    }
  }, [joinCode]);

  useEffect(() => {
    void refresh();
    const id = setInterval(() => void refresh(), 2000);
    return () => clearInterval(id);
  }, [refresh]);

  const options = normalizeOptions(session?.options || session?.Options);

  const onCast = async (choice, writeInText) => {
    if (!session?.id || busy) return;
    setBusy(true);
    setError(null);
    try {
      setStoredDisplayName(displayName);
      const result = await castVoteBallot({
        sessionId: session.id,
        deviceToken,
        choice,
        displayName,
        writeInText,
      });
      setCastResult(result);
    } catch (err) {
      setError(err?.message || 'Could not cast vote');
    } finally {
      setBusy(false);
    }
  };

  const onSelectOption = (value) => {
    if (value === '__write_in__') {
      setWriteInMode(true);
      return;
    }
    void onCast(value);
  };

  const onSubmitWriteIn = () => {
    const name = writeInName.trim();
    if (!name) return;
    void onCast('__write_in__', name);
  };

  if (!hasSupabaseConfig()) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-pp-paper px-4 text-pp-ink">
        <p className="text-sm text-pp-muted">Voting remote is unavailable in this environment.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-pp-paper px-4 py-8 text-pp-ink">
      <div className="mx-auto w-full max-w-md space-y-6">
        <header className="text-center">
          <p className="text-xs font-semibold uppercase tracking-wide text-pp-muted">
            Pocket Parliamentarian
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">Voting remote</h1>
          <p className="mt-1 font-mono text-lg tracking-widest text-pp-accent">{joinCode}</p>
        </header>

        <label className="block rounded-xl border border-pp-line bg-pp-surface p-4 shadow-pp">
          <span className="text-xs font-medium text-pp-muted">Your name (optional)</span>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="mt-1 w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
            placeholder="e.g. Jordan"
            autoComplete="name"
          />
        </label>

        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        {!room && !error && (
          <p className="text-center text-sm text-pp-muted">Connecting to the meeting room…</p>
        )}

        {room && !session && (
          <div className="rounded-xl border border-pp-line bg-pp-surface p-6 text-center shadow-pp">
            <p className="text-base font-medium">Waiting for the chair to open a vote…</p>
            <p className="mt-2 text-sm text-pp-muted">
              Keep this page open. When a vote or election ballot opens, choices will appear here.
            </p>
          </div>
        )}

        {session && castResult && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center">
            <p className="text-lg font-semibold text-emerald-900">
              {castResult.alreadyCast ? 'Vote already recorded' : 'Vote recorded'}
            </p>
            <p className="mt-2 text-sm text-emerald-800">
              Your choice:{' '}
              <span className="font-semibold">
                {castResult.writeInText || formatChoiceLabel(castResult.choice)}
              </span>
            </p>
            <p className="mt-3 text-xs text-emerald-700">
              One vote per device. Waiting for the chair to close this ballot…
            </p>
          </div>
        )}

        {session && !castResult && (
          <div className="space-y-4 rounded-xl border border-pp-line bg-pp-surface p-5 shadow-pp">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-pp-muted">
                {session.kind === 'election' ? 'Election ballot' : 'Motion vote'}
              </p>
              <p className="mt-1 text-base font-medium leading-snug">
                {session.questionText || session.question_text || 'Vote'}
              </p>
            </div>
            <div className="grid gap-2">
              {options.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  disabled={busy}
                  onClick={() => onSelectOption(opt.value)}
                  className={`rounded-xl px-4 py-4 text-base font-semibold hover:opacity-90 disabled:opacity-50 ${
                    opt.value === '__write_in__'
                      ? 'border border-dashed border-pp-line bg-pp-paper text-pp-ink'
                      : 'bg-pp-ink text-pp-surface'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {writeInMode && (
              <div className="space-y-2 rounded-xl border border-pp-line bg-pp-paper p-3">
                <label className="block text-xs font-medium text-pp-muted">
                  Write-in candidate name
                </label>
                <input
                  type="text"
                  autoFocus
                  value={writeInName}
                  onChange={(e) => setWriteInName(e.target.value)}
                  className="w-full rounded-lg border border-pp-line bg-white px-3 py-2 text-sm"
                  placeholder="Type a name"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={busy || !writeInName.trim()}
                    onClick={onSubmitWriteIn}
                    className="flex-1 rounded-lg bg-pp-ink px-3 py-2 text-sm font-semibold text-pp-surface hover:opacity-90 disabled:opacity-50"
                  >
                    Submit write-in
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setWriteInMode(false);
                      setWriteInName('');
                    }}
                    className="rounded-lg border border-pp-line px-3 py-2 text-sm text-pp-ink hover:bg-white"
                  >
                    Back
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
